import React, { useMemo } from 'react';

// 타입 정렬 순서 상수 (컴포넌트 외부로 이동하여 재생성 방지)
const TYPE_ORDER = {
    '스피닝 릴': 1,
    '로우프로파일 베이트캐스팅 릴': 2,
    '클래식 베이트캐스팅 릴': 3,
    '컨벤셔널 릴': 4
};

// 테이블 뷰 컴포넌트 - 최적화된 성능
const ReelTableView = React.memo(({ reels, reelSizes, searchTerm }) => {
    // 기본 테이블 데이터
    const baseTableData = useMemo(() => {
        const data = [];
        
        reels.forEach(reel => {
            const sizes = reelSizes.filter(size => size.reel_id === reel.id);
            
            if (sizes.length === 0) {
                // 크기 정보가 없는 릴도 표시
                data.push({
                    id: `${reel.id}-no-size`,
                    reel_name: reel.name,
                    reel_type: reel.reel_type,
                    size: '정보 없음',
                    test_value: '',
                    ratio_value: '',
                    capacity_value: '',
                    speed_value: '',
                    drag_max_value: '',
                    internal_load_value: '',
                    engine_power: '',
                    req_levels: '',
                    price_silver: reel.price_silver || '',
                    price_gold: reel.price_gold || '',
                    sea_compatibility: reel.sea_compatibility,
                    sizeName: ''
                });
            } else {
                sizes.forEach((size, sizeIndex) => {
                    // undefined 방지를 위한 안전한 키 생성
                    const safeId = `${reel.id}-${size.name || size.id || sizeIndex}`;
                    data.push({
                        id: safeId,
                        reel_name: reel.name,
                        reel_type: reel.reel_type,
                        size: size.size || '',
                        test_value: size.test_value || '',
                        test_value_mod1: size.test_value_mod1 || '',
                        test_value_mod2: size.test_value_mod2 || '',
                        ratio_value: size.ratio_value || '',
                        ratio_value_mod1: size.ratio_value_mod1 || '',
                        ratio_value_mod2: size.ratio_value_mod2 || '',
                        capacity_value: size.capacity_value || '',
                        capacity_value_mod1: size.capacity_value_mod1 || '',
                        capacity_value_mod2: size.capacity_value_mod2 || '',
                        speed_value: size.speed_value || '',
                        speed_value_mod1: size.speed_value_mod1 || '',
                        speed_value_mod2: size.speed_value_mod2 || '',
                        drag_max_value: size.drag_max_value || '',
                        drag_max_value_mod1: size.drag_max_value_mod1 || '',
                        drag_max_value_mod2: size.drag_max_value_mod2 || '',
                        internal_load_value: size.internal_load_value || '',
                        internal_load_value_mod1: size.internal_load_value_mod1 || '',
                        internal_load_value_mod2: size.internal_load_value_mod2 || '',
                        engine_power: size.engine_power || '',
                        req_levels: size.req_levels || '',
                        price_silver: size.price_silver || '',
                        price_gold: size.price_gold || '',
                        sea_compatibility: size.issea,
                        sizeName: size.name || ''
                    });
                });
            }
        });
        
        // 타입과 이름으로 정렬 (한 번만 수행)
        data.sort((a, b) => {
            // 먼저 타입으로 정렬
            const typeComparison = (TYPE_ORDER[a.reel_type] || 999) - (TYPE_ORDER[b.reel_type] || 999);
            if (typeComparison !== 0) {
                return typeComparison;
            }
            
            // 타입이 같으면 이름으로 정렬
            return a.reel_name.localeCompare(b.reel_name);
        });
        
        return data;
    }, [reels, reelSizes]); // searchTerm 제거

    // 검색 필터링 (더 빠른 처리)
    const filteredTableData = useMemo(() => {
        if (!searchTerm.trim()) {
            return baseTableData;
        }
        
        const searchLower = searchTerm.toLowerCase();
        
        return baseTableData.filter(row => {
            return (
                (row.reel_name && row.reel_name.toLowerCase().includes(searchLower)) ||
                (row.reel_type && row.reel_type.toLowerCase().includes(searchLower)) ||
                (row.size && row.size.toLowerCase().includes(searchLower)) ||
                (row.test_value && row.test_value.toLowerCase().includes(searchLower)) ||
                (row.ratio_value && row.ratio_value.toLowerCase().includes(searchLower)) ||
                (row.capacity_value && row.capacity_value.toLowerCase().includes(searchLower)) ||
                (row.speed_value && row.speed_value.toLowerCase().includes(searchLower)) ||
                (row.drag_max_value && row.drag_max_value.toLowerCase().includes(searchLower)) ||
                (row.internal_load_value && row.internal_load_value.toLowerCase().includes(searchLower)) ||
                (row.engine_power && row.engine_power.toString().includes(searchLower)) ||
                (row.req_levels && row.req_levels.toString().includes(searchLower)) ||
                (row.price_silver && row.price_silver.toString().includes(searchLower)) ||
                (row.price_gold && row.price_gold.toString().includes(searchLower)) ||
                (row.sizeName && row.sizeName.toLowerCase().includes(searchLower))
            );
        });
    }, [baseTableData, searchTerm]);

    // 컬럼 유효성 검사 (기본 데이터만 기준으로 계산, 한 번만)
    const hasValidData = useMemo(() => {
        if (baseTableData.length === 0) return {};
        
        const validity = {
            test_value: baseTableData.some(row => row.test_value && row.test_value !== ''),
            ratio_value: baseTableData.some(row => row.ratio_value && row.ratio_value !== ''),
            capacity_value: baseTableData.some(row => row.capacity_value && row.capacity_value !== ''),
            speed_value: baseTableData.some(row => row.speed_value && row.speed_value !== ''),
            drag_max_value: baseTableData.some(row => row.drag_max_value && row.drag_max_value !== ''),
            internal_load_value: baseTableData.some(row => row.internal_load_value && row.internal_load_value !== ''),
            engine_power: baseTableData.some(row => row.engine_power && row.engine_power !== ''),
            req_levels: baseTableData.some(row => row.req_levels && row.req_levels !== ''),
            price_silver: baseTableData.some(row => row.price_silver && row.price_silver !== ''),
            price_gold: baseTableData.some(row => row.price_gold && row.price_gold !== '')
        };
        
        return validity;
    }, [baseTableData]); // filteredTableData 대신 baseTableData 사용

    if (filteredTableData.length === 0) {
        return (
            <div className="no-data">
                <p>{searchTerm ? '검색 결과가 없습니다.' : '표시할 데이터가 없습니다.'}</p>
            </div>
        );
    }

    return (
        <div className="table-container">
            <div className="table-wrapper">
                <table className="reel-table">
                    <thead>
                        <tr>
                            <th>타입</th>
                            <th>릴 이름</th>
                            <th>크기</th>
                            <th>해수저항</th>
                            {hasValidData.test_value && <th>테스트</th>}
                            {hasValidData.ratio_value && <th>기어비</th>}
                            {hasValidData.capacity_value && <th>용량</th>}
                            {hasValidData.speed_value && <th>속도</th>}
                            {hasValidData.drag_max_value && <th>최대저항</th>}
                            {hasValidData.internal_load_value && <th>내부하중</th>}
                            {hasValidData.engine_power && <th>엔진파워</th>}
                            {hasValidData.req_levels && <th>필요레벨</th>}
                            {hasValidData.price_silver && <th>실버 가격</th>}
                            {hasValidData.price_gold && <th>골드 가격</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {filteredTableData.map((row) => (
                            <tr key={row.id}>
                                <td style={{ paddingLeft: '12px' }}>{row.reel_type}</td>
                                <td className="reel-name">{row.reel_name}</td>
                                <td className="size-cell">{row.size}</td>
                                <td className="sea-cell">
                                    {row.sea_compatibility ? '💧' : '❌'}
                                </td>
                                {hasValidData.test_value && (
                                    <td>
                                        <div>{row.test_value}</div>
                                        {row.test_value_mod1 && <div style={{ marginLeft: '12px', fontSize: '0.9em', color: '#666' }}>개조1: {row.test_value_mod1}</div>}
                                        {row.test_value_mod2 && <div style={{ marginLeft: '12px', fontSize: '0.9em', color: '#666' }}>개조2: {row.test_value_mod2}</div>}
                                    </td>
                                )}
                                {hasValidData.ratio_value && (
                                    <td>
                                        <div>{row.ratio_value ? `${row.ratio_value}` : ''}</div>
                                        {row.ratio_value_mod1 && <div style={{ marginLeft: '12px', fontSize: '0.9em', color: '#666' }}>개조1: {row.ratio_value_mod1}</div>}
                                        {row.ratio_value_mod2 && <div style={{ marginLeft: '12px', fontSize: '0.9em', color: '#666' }}>개조2: {row.ratio_value_mod2}</div>}
                                    </td>
                                )}
                                {hasValidData.capacity_value && (
                                    <td>
                                        <div>{row.capacity_value}</div>
                                        {row.capacity_value_mod1 && <div style={{ marginLeft: '12px', fontSize: '0.9em', color: '#666' }}>개조1: {row.capacity_value_mod1}</div>}
                                        {row.capacity_value_mod2 && <div style={{ marginLeft: '12px', fontSize: '0.9em', color: '#666' }}>개조2: {row.capacity_value_mod2}</div>}
                                    </td>
                                )}
                                {hasValidData.speed_value && (
                                    <td>
                                        <div>{row.speed_value}</div>
                                        {row.speed_value_mod1 && <div style={{ marginLeft: '12px', fontSize: '0.9em', color: '#666' }}>개조1: {row.speed_value_mod1}</div>}
                                        {row.speed_value_mod2 && <div style={{ marginLeft: '12px', fontSize: '0.9em', color: '#666' }}>개조2: {row.speed_value_mod2}</div>}
                                    </td>
                                )}
                                {hasValidData.drag_max_value && (
                                    <td>
                                        <div>{row.drag_max_value ? `${row.drag_max_value}` : ''}</div>
                                        {row.drag_max_value_mod1 && <div style={{ marginLeft: '12px', fontSize: '0.9em', color: '#666' }}>개조1: {row.drag_max_value_mod1}</div>}
                                        {row.drag_max_value_mod2 && <div style={{ marginLeft: '12px', fontSize: '0.9em', color: '#666' }}>개조2: {row.drag_max_value_mod2}</div>}
                                    </td>
                                )}
                                {hasValidData.internal_load_value && (
                                    <td>
                                        <div>{row.internal_load_value}</div>
                                        {row.internal_load_value_mod1 && <div style={{ marginLeft: '12px', fontSize: '0.9em', color: '#666' }}>개조1: {row.internal_load_value_mod1}</div>}
                                        {row.internal_load_value_mod2 && <div style={{ marginLeft: '12px', fontSize: '0.9em', color: '#666' }}>개조2: {row.internal_load_value_mod2}</div>}
                                    </td>
                                )}
                                {hasValidData.engine_power && <td>{row.engine_power}</td>}
                                {hasValidData.req_levels && <td>{row.req_levels}</td>}
                                {hasValidData.price_silver && (
                                    <td>
                                        {row.price_silver ? row.price_silver.toLocaleString() : ''}
                                    </td>
                                )}
                                {hasValidData.price_gold && (
                                    <td>
                                        {row.price_gold ? row.price_gold.toLocaleString() : ''}
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
});

// 🚀 성능 최적화: 커스텀 비교 함수로 불필요한 재렌더링 방지
const arePropsEqual = (prevProps, nextProps) => {
    const isEqual = (
        prevProps.reels.length === nextProps.reels.length &&
        prevProps.reelSizes.length === nextProps.reelSizes.length &&
        prevProps.searchTerm === nextProps.searchTerm &&
        prevProps.reels === nextProps.reels && // 참조 비교
        prevProps.reelSizes === nextProps.reelSizes // 참조 비교
    );
    
    return isEqual;
};

export default React.memo(ReelTableView, arePropsEqual);
